import { UseMutationResult } from '@tanstack/react-query';
import React, { createContext, ReactNode, useContext } from 'react';

import { TempBreadCrumbsDTO } from '@/src/components/project/Library/types';

import { FILE_TASK_LINK_KEY, InboxEntryStatus } from '../../../../types';

export type SidebarActions = {
  recordFileMutation: UseMutationResult<any, any, { record: boolean }, any>;
  markMiscellaneousMutation: UseMutationResult<any, any, { misc: boolean }, any>;
  markReadMutation: UseMutationResult<any, any, { read: boolean }, any>;
  markReadMutationListener: UseMutationResult<any, any, { read: boolean }, any>;
  updateStatusMutation: UseMutationResult<any, any, { status: InboxEntryStatus }, any>;
  updateStatusMutationListener: UseMutationResult<any, any, { status: InboxEntryStatus }, any>;
  tagFileWorkstreamMutation: UseMutationResult<any, any, { workstreamIds: number[] }, any>;
  renameFileMutation?: UseMutationResult<any, any, { newName: string }, any>;
  duplicateFileMutation: UseMutationResult<any, any, any, any>;
  createTask: {
    linkId: number;
    taskLinkKey: FILE_TASK_LINK_KEY;
    onTaskCreated: (tasks: any[]) => void;
    onTaskUpdated: (task: any) => void;
    onTaskDeleted: ({ deletedTaskIds }: { deletedTaskIds: number[] }) => void;
  };
  onFileArchived: ({
    destinationFolderId,
    newFolderPath,
    tempBreadCrumbs,
  }: {
    destinationFolderId: number;
    newFolderPath: string;
    tempBreadCrumbs: TempBreadCrumbsDTO;
  }) => void;
  onCopyFileUrl: () => string;
  onFileUrlCopied: () => void;
  onFileUrlDownloaded: () => void;
};

type FileEntryViewerSidebarActionsContextType = {
  sidebarActions: SidebarActions;
};

const FileEntryViewerSidebarActionsContext = createContext<
  FileEntryViewerSidebarActionsContextType | undefined
>(undefined);

type FileEntryViewerSidebarActionsProviderProps = {
  sidebarActions: SidebarActions;
  children: ReactNode;
};

export const FileEntryViewerSidebarActionsProvider = ({
  sidebarActions,
  children,
}: FileEntryViewerSidebarActionsProviderProps) => {
  return (
    <FileEntryViewerSidebarActionsContext.Provider
      value={{
        sidebarActions,
      }}
    >
      {children}
    </FileEntryViewerSidebarActionsContext.Provider>
  );
};

export function useFileEntryViewerSidebarActions() {
  const context = useContext(FileEntryViewerSidebarActionsContext);
  if (!context) {
    throw new Error(
      'useFileEntryViewerSidebarActions must be used within a FileEntryViewerSidebarActionsProvider'
    );
  }
  return context;
}
